import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon, MessageSquareIcon } from '@utils/icons';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import type { CommsMember } from '@apis/comms';
import type { ChannelSummary } from '@interfaces/ICommsApi';
import { channelLabel } from '../../utils/channelLabel';
import { isChannelUnread } from '../../utils/isChannelUnread';
import { withPreviewAuthorName } from '../../utils/memberName';
import styles from './ChatList.module.css';
import { ChatBox } from '../ChatBox/ChatBox';
import { IntegrationsFooter } from '../IntegrationsFooter/IntegrationsFooter';

type KindFilter = 'all' | 'dms' | 'channels';

interface IChatListProps {
  siteId: string;
  channels: ChannelSummary[];
  members: CommsMember[];
  currentUserId: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreateClick: () => void;
  lastReadMap: Record<string, string>;
  countMap: Record<string, number>;
}

export function ChatList({ channels, members, currentUserId, selectedId, onSelect, onCreateClick, lastReadMap, countMap }: IChatListProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');

  // Count channels that have unread activity rather than summing message counts
  const unreadChannelsCount = useMemo(
    () =>
      channels.reduce((acc, c) => {
        if (c.id === selectedId) return acc;
        const count = countMap[c.id] ?? 0;
        const unread =
          count > 0 || isChannelUnread(c.lastMessageAt, lastReadMap[c.id]);
        return acc + (unread ? 1 : 0);
      }, 0),
    [channels, selectedId, countMap, lastReadMap],
  );
  const totalLabel = unreadChannelsCount > 99 ? '99+' : String(unreadChannelsCount);

  const visible = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return channels.filter(
      (channel) =>
        (kind === 'all' || (kind === 'dms') === (channel.kind === 'dm')) &&
        channelLabel(channel, currentUserId, members).toLowerCase().includes(normalizedQuery),
    );
  }, [channels, members, currentUserId, query, kind]);

  function renderChannel(channel: ChannelSummary) {
    // The active channel never shows an unread indicator
    const isActive = channel.id === selectedId;
    const count = isActive ? 0 : (countMap[channel.id] ?? 0);
    const hasUnread =
      !isActive &&
      (count > 0 || isChannelUnread(channel.lastMessageAt, lastReadMap[channel.id]));
    return (
      <ChatBox
        key={channel.id}
        channel={withPreviewAuthorName(channel, members)}
        label={channelLabel(channel, currentUserId, members)}
        selectedId={selectedId}
        onSelect={onSelect}
        hasUnread={hasUnread}
        unreadCount={count}
      />
    );
  }

  return (
    <aside className={styles.root}>
      <header className={styles.header}>
        <span className={styles.headerIcon}><MessageSquareIcon size={16} /></span>
        <span className={styles.headerTitle}>{t('comms.channels.title')}</span>

        {unreadChannelsCount > 0 && (
          <span
            className={styles.headerCount}
            aria-label={t('comms.channels.unreadTotal', { count: unreadChannelsCount })}
          >
            {totalLabel}
          </span>
        )}

        <button
          type="button"
          className={styles.createButton}
          title={t('comms.channels.createTooltip')}
          onClick={onCreateClick}
        >
          <span className={styles.createIcon}>+</span>
        </button>
      </header>

      <div className={styles.searchRow}>
        <span className={styles.searchIcon}><SearchIcon size={14} /></span>
        <input
          className={styles.searchInput}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('comms.channels.searchPlaceholder')}
          aria-label={t('comms.channels.searchPlaceholder')}
        />
      </div>

      <div className={styles.filterRow}>
        <SegmentedControl
          value={kind}
          onChange={setKind}
          size="sm"
          ariaLabel={t('comms.channels.filterLabel')}
          options={[
            { id: 'all', label: t('comms.channels.filterAll') },
            { id: 'dms', label: t('comms.channels.filterDms') },
            { id: 'channels', label: t('comms.channels.filterChannels') },
          ]}
        />
      </div>

      <ScrollArea className={styles.groups} viewportClassName={styles.groupsBody}>
        {channels.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}><MessageSquareIcon size={28} /></span>
            <div className={styles.emptyTitle}>{t('comms.channels.empty')}</div>
            <div className={styles.emptyHint}>{t('comms.channels.emptyHint')}</div>
          </div>
        ) : visible.length === 0 ? (
          <div className={styles.noResults}>
            {query.trim() !== ''
              ? t('comms.channels.noSearchResults', { query: query.trim() })
              : t(kind === 'dms' ? 'comms.channels.noDms' : 'comms.channels.noChannels')}
          </div>
        ) : (
          visible.map(renderChannel)
        )}
      </ScrollArea>

      <IntegrationsFooter />
    </aside>
  );
}
